import { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Options } from "@popperjs/core";
import { noop } from "lodash";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Combobox } from "@circle-react-uikit/Combobox";
import { Typography } from "@circle-react-uikit/Typography";
import { popperSameWidthModifier } from "../Dropdown/utils";
import { defaultDisplayValue } from "./defaultDisplayValue";

export interface SearchableSelectProps<T = unknown> {
  appendTo?: HTMLElement | null;
  children: ReactNode;
  className?: string;
  displayValue?: (option: T) => string;
  hideCaret?: boolean;
  isError?: boolean;
  label?: ReactNode;
  onChange?: (option: T) => void;
  onClose?: () => void;
  onFilter?: (value: string) => void;
  placeholder?: string;
  popperOptions?: Partial<Options>;
  tooltipText?: string | null;
  value?: T | null;
}

const SearchableSelectComponent = <T,>({
  appendTo = null,
  children,
  displayValue = defaultDisplayValue,
  hideCaret = false,
  isError = false,
  label = "",
  onChange = noop,
  onClose = noop,
  onFilter = noop,
  placeholder = t("select_an_option"),
  popperOptions = {},
  tooltipText = null,
  value,
  ...rest
}: SearchableSelectProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes, forceUpdate } = usePopper(
    targetElement,
    popperElement,
    {
      placement: "bottom",
      strategy: "fixed",
      modifiers: [popperSameWidthModifier],
      ...popperOptions,
    },
  );

  const renderList = (
    <div
      className="z-10"
      ref={setPopperElement}
      style={styles.popper}
      {...attributes.popper}
    >
      <Combobox.Options onClose={onClose}>
        <Combobox.OptionsScrollableContainer>
          {children}
        </Combobox.OptionsScrollableContainer>
      </Combobox.Options>
    </div>
  );

  return (
    <Combobox {...rest} value={value} onChange={onChange}>
      {({ open }: { open: boolean }) => {
        if (open !== isOpen) {
          setIsOpen(open);

          if (open && forceUpdate) {
            // Defer to next frame so targetElement ref is committed before Popper calculates position
            requestAnimationFrame(() => {
              forceUpdate();
            });
          }
        }

        return (
          <>
            {label && (
              <Combobox.Label className="mb-2">
                <Typography.LabelSm weight="semibold">
                  {label}
                </Typography.LabelSm>
                {tooltipText && (
                  <span className="ml-2 inline-flex">
                    <TippyV2 content={tooltipText}>
                      <Icon type="question-mark" className="icon icon--xs" />
                    </TippyV2>
                  </span>
                )}
              </Combobox.Label>
            )}
            <Combobox.InputWrapper ref={setTargetElement} isError={isError}>
              <Combobox.Input
                displayValue={displayValue}
                onChange={event => onFilter(event.target.value)}
                placeholder={placeholder}
                onFocus={() => {
                  if (open) return;
                  buttonRef.current?.click();
                }}
                onClick={() => {
                  if (open) return;
                  buttonRef.current?.click();
                }}
              />
              {!hideCaret && <Combobox.ChevronDownButton ref={buttonRef} />}
            </Combobox.InputWrapper>
            {!appendTo && renderList}
            {!!appendTo && createPortal(renderList, appendTo)}
          </>
        );
      }}
    </Combobox>
  );
};

export const SearchableSelect = Object.assign(SearchableSelectComponent, {
  EmptyOption: Combobox.EmptyOption,
  Option: Combobox.Option,
});
